import React, { useMemo } from 'react';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  Alert, 
  Grid, 
  Tooltip,
  alpha,
  Stack
} from "@mui/material";
import { useState, useEffect } from 'react';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Define Company Primary Color
const COMPANY_PRIMARY_COLOR = '#0101FF';

export default function CreateIdealCandidateForm({
  onSubmit = values => console.log("Default onSubmit from CreateIdealCandidateForm:", values),
  viewOnly = false,
  initialData = null
}) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const currentAccent = COMPANY_PRIMARY_COLOR; 
  const [formErrorsSummary, setFormErrorsSummary] = useState([]);

  const steps = ['Job Details', 'Skills', 'Behaviors'];
  const BORDER_RADIUS_VALUE = '20px'; 
  const BORDER_RADIUS_SMALLER = '12px';


  const formInitialValues = useMemo(() => initialData ? {
    title: initialData.title || '',
    description: initialData.description || '',
    skills: initialData.skills && initialData.skills.length > 0 ? initialData.skills.map(s => ({ name: s.name || '', weight: s.weight || 1})) : [{ name: '', weight: 1 }],
    behaviors: initialData.behaviors && initialData.behaviors.length > 0 ? initialData.behaviors.map(b => ({ name: b.name || '', weight: b.weight || 1})) : [{ name: '', weight: 1 }],
    accentColor: COMPANY_PRIMARY_COLOR 
  } : {
    title: '',
    description: '',
    skills: [{ name: '', weight: 1 }],
    behaviors: [{ name: '', weight: 1 }],
    accentColor: COMPANY_PRIMARY_COLOR 
  }, [initialData]);


  const validationSchema = Yup.object().shape({
    title: Yup.string().min(3, 'Too Short!').required('Job title is required'),
    description: Yup.string().max(1000, 'Too Long!'), 
    skills: Yup.array()
      .of(Yup.object().shape({
        name: Yup.string().min(2, 'Too short').required('Skill name is required'),
        weight: Yup.number().integer('Must be integer').min(0, 'Min 0').max(10, 'Max 10').required('Weight is required')
      }))
      .min(1, 'At least one skill is required'),
    behaviors: Yup.array()
      .of(Yup.object().shape({
        name: Yup.string().min(2, 'Too short').required('Behavior name is required'),
        weight: Yup.number().integer('Must be integer').min(0, 'Min 0').max(10, 'Max 10').required('Weight is required')
      }))
      .min(1, 'At least one behavior is required'),
  });

  const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);
  const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

  const handleSubmitForm = (values, actions) => {
    validationSchema.validate(values, { abortEarly: false })
      .then(() => {
        setFormErrorsSummary([]); 
        const submissionValues = {...values, accentColor: COMPANY_PRIMARY_COLOR };
        onSubmit(submissionValues);
        actions.setSubmitting(false);
      })
      .catch(err => {
        const friendlyErrors = err.inner.map(e => {
            let path = e.path.replace(/\[(\d+)\]\./g, (match, index) => `${e.path.split('.')[0]} ${parseInt(index) + 1} `);
            path = path.charAt(0).toUpperCase() + path.slice(1); 
            return `${path}: ${e.message}`;
        });
        setFormErrorsSummary(friendlyErrors);
        actions.setSubmitting(false); 
        actions.setErrors(err.inner.reduce((acc, error) => {
          acc[error.path] = error.message;
          return acc;
        }, {}));
      });
  };


  // --- View Only Mode ---
  if (viewOnly) {
    const displayValues = formInitialValues; 
    const displayAccent = COMPANY_PRIMARY_COLOR;

    return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: {xs: 2.5, sm: 3.5}, // Slightly increased padding
        backgroundColor: 'background.paper', 
        borderRadius: BORDER_RADIUS_VALUE, 
        border: (theme) => `1px solid ${theme.palette.divider}` 
      }}
    >
      {/* Job Details Section */}
      <Box mb={4}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ borderBottom: (theme) => `1px solid ${alpha(displayAccent, 0.4)}`, pb: 1, mb: 2.5 }}>
          {/* <ArticleOutlinedIcon sx={{ color: displayAccent }} /> Uncomment when icon is imported */}
          <Typography 
            variant="h6"
            sx={{ 
              color: 'text.primary', 
              fontWeight: 500,
            }}
          >
            Job Details
          </Typography>
        </Stack>
        <Grid container spacing={2.5}> {/* Slightly increased spacing */}
          <Grid item xs={12} sm={3}>
            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              Job Title:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography variant="body1" sx={{ color: 'text.primary' }}>
              {displayValues.title || "N/A"}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              Description:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.primary', 
                whiteSpace: 'pre-wrap', 
                maxHeight: 200, 
                overflowY: 'auto',
                lineHeight: 1.65
              }}
            >
              {displayValues.description || "N/A"}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Skills Section */}
      <Box mb={4}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ borderBottom: (theme) => `1px solid ${alpha(displayAccent, 0.4)}`, pb: 1, mb: 2.5 }}>
          {/* <ConstructionOutlinedIcon sx={{ color: displayAccent }} /> Uncomment when icon is imported */}
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.primary', 
              fontWeight: 500,
            }}
          >
            Skills
          </Typography>
        </Stack>
        {displayValues.skills && displayValues.skills.filter(s => s.name).length > 0 ? (
          <Stack spacing={1}> {/* Use Stack for spacing between skill items */}
            {displayValues.skills.filter(s => s.name).map((skill, i) => (
              <Paper 
                key={`skill-view-${i}`} 
                elevation={0} // Flat
                sx={{ 
                  p: 1.5, 
                  borderRadius: BORDER_RADIUS_SMALLER, 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: (theme) => alpha(displayAccent, 0.05), // Subtle background tint using displayAccent
                  border: (theme) => `1px solid ${alpha(displayAccent, 0.2)}` // Optional: border with accent
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {skill.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Weight: {skill.weight}
                </Typography>
              </Paper>
            ))}
          </Stack>
        ) : ( 
          <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
            No skills defined.
          </Typography> 
        )}
      </Box>

      {/* Behaviors Section */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ borderBottom: (theme) => `1px solid ${alpha(displayAccent, 0.4)}`, pb: 1, mb: 2.5 }}>
          {/* <AccountTreeOutlinedIcon sx={{ color: displayAccent }} /> Uncomment when icon is imported */}
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.primary', 
              fontWeight: 500,
            }}
          >
            Behaviors
          </Typography>
        </Stack>
        {displayValues.behaviors && displayValues.behaviors.filter(b => b.name).length > 0 ? (
          <Stack spacing={1}> {/* Use Stack for spacing between behavior items */}
            {displayValues.behaviors.filter(b => b.name).map((behavior, i) => (
              <Paper 
                key={`behavior-view-${i}`} 
                elevation={0} // Flat
                sx={{ 
                  p: 1.5, 
                  borderRadius: BORDER_RADIUS_SMALLER, 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: (theme) => alpha(displayAccent, 0.05), // Subtle background tint
                  border: (theme) => `1px solid ${alpha(displayAccent, 0.2)}` // Optional: border with accent
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {behavior.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Weight: {behavior.weight}
                </Typography>
              </Paper>
            ))}
          </Stack>
        ) : ( 
          <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
            No behaviors defined.
          </Typography> 
        )}
      </Box>
    </Paper>
  );
  }

  // --- Edit/Create Mode ---
  return (
    <> 
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, mt: 2 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel StepIconProps={{ sx: { '&.Mui-active': { color: currentAccent }, '&.Mui-completed': { color: currentAccent }, '& .MuiStepIcon-text': { fill: theme.palette.getContrastText(currentAccent) } } }}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {formErrorsSummary.length > 0 && activeStep === steps.length -1 && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: BORDER_RADIUS_SMALLER }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb:1 }}>Please review the following errors:</Typography>
          <ul style={{ marginTop: 0, paddingLeft: 20, fontSize: '0.875rem' }}>
            {formErrorsSummary.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </Alert>
      )}

      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmitForm}
        enableReinitialize
      >
        {({ values, touched, errors, isSubmitting, dirty, isValid, setFieldValue }) => (
          <Form autoComplete="off">
            <Box sx={{minHeight: 380, mb:3}}> {/* Increased minHeight for more space */}
              {activeStep === 0 && ( 
                <Box>
                  <Typography variant="h6" sx={{ mb: 2.5, color: currentAccent, fontWeight: 600 }}>
                    Job Details
                    <Tooltip title="Basic information about the role.">
                      <IconButton size="small" sx={{ ml: 0.5, color: 'text.secondary' }}><HelpOutlineIcon fontSize="inherit" /></IconButton>
                    </Tooltip>
                  </Typography>
                  <Field
                    as={TextField} fullWidth variant="filled"
                    label="Job Title" name="title"
                    helperText={<ErrorMessage name="title">{msg => <Typography variant="caption" color="error">{msg}</Typography>}</ErrorMessage>}
                    error={touched.title && Boolean(errors.title)}
                    sx={{ mb: 3, '& .MuiFilledInput-root': { borderRadius: BORDER_RADIUS_SMALLER } }} 
                  />
                  <Field
                    as={TextField} fullWidth multiline rows={6} variant="filled" 
                    label="Job Description (Optional)" name="description"
                    helperText={<ErrorMessage name="description">{msg => <Typography variant="caption" color="error">{msg}</Typography>}</ErrorMessage>}
                    error={touched.description && Boolean(errors.description)}
                    sx={{ '& .MuiFilledInput-root': { borderRadius: BORDER_RADIUS_SMALLER } }}
                  />
                </Box>
              )}

              {activeStep === 1 && ( 
                <Box>
                  <Typography variant="h6" sx={{ mb: 2.5, color: currentAccent, fontWeight: 600 }}>
                    Skills
                    <Tooltip title="List key skills and their importance (1-10).">
                        <IconButton size="small" sx={{ ml: 0.5, color: 'text.secondary' }}><HelpOutlineIcon fontSize="inherit" /></IconButton>
                    </Tooltip>
                  </Typography>
                  <FieldArray name="skills">
                    {({ remove, push }) => (
                      <Box>
                        {values.skills.map((skill, index) => (
                          <Paper key={index} elevation={1} sx={{ p: 2.5, mb: 2.5, borderRadius: BORDER_RADIUS_SMALLER, backgroundColor: alpha(currentAccent, 0.03) }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={7}>
                                <Field
                                  as={TextField} fullWidth variant="filled" size="small"
                                  label={`Skill ${index + 1}`} name={`skills[${index}].name`}
                                  helperText={<ErrorMessage name={`skills[${index}].name`}>{msg => <Typography variant="caption" color="error">{msg}</Typography>}</ErrorMessage>}
                                  error={touched.skills?.[index]?.name && Boolean(errors.skills?.[index]?.name)}
                                  sx={{ '& .MuiFilledInput-root': { borderRadius: BORDER_RADIUS_SMALLER } }}
                                />
                              </Grid>
                              <Grid item xs={9} sm={3}>
                                <Field
                                  as={TextField} fullWidth variant="filled" size="small"
                                  label="Weight (1-10)" type="number" name={`skills[${index}].weight`}
                                  inputProps={{ min: 1, max: 10 }}
                                  helperText={<ErrorMessage name={`skills[${index}].weight`}>{msg => <Typography variant="caption" color="error">{msg}</Typography>}</ErrorMessage>}
                                  error={touched.skills?.[index]?.weight && Boolean(errors.skills?.[index]?.weight)}
                                  sx={{ '& .MuiFilledInput-root': { borderRadius: BORDER_RADIUS_SMALLER } }}
                                />
                              </Grid>
                              <Grid item xs={3} sm={2} textAlign="right">
                                <IconButton onClick={() => values.skills.length > 1 && remove(index)} disabled={values.skills.length <= 1} aria-label="Remove skill">
                                  <DeleteForeverOutlinedIcon color={values.skills.length <= 1 ? "disabled" : "action"}/>
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Paper>
                        ))}
                        <Button
                          variant="outlined" startIcon={<AddIcon />}
                          onClick={() => push({ name: '', weight: 1 })}
                          sx={{ textTransform: 'none', color: currentAccent, borderColor: currentAccent, mt: 1, borderRadius: BORDER_RADIUS_VALUE }}
                        > Add Skill </Button>
                      </Box>
                    )}
                  </FieldArray>
                </Box>
              )}

              {activeStep === 2 && ( 
                <Box>
                  <Typography variant="h6" sx={{ mb: 2.5, color: currentAccent, fontWeight: 600 }}>
                    Behaviors
                      <Tooltip title="Define important behaviors and their weights (1-5).">
                        <IconButton size="small" sx={{ ml: 0.5, color: 'text.secondary' }}><HelpOutlineIcon fontSize="inherit" /></IconButton>
                    </Tooltip>
                  </Typography>
                  <FieldArray name="behaviors">
                      {({ remove, push }) => (
                      <Box>
                        {values.behaviors.map((behavior, index) => (
                          <Paper key={index} elevation={1} sx={{ p: 2.5, mb: 2.5, borderRadius: BORDER_RADIUS_SMALLER, backgroundColor: alpha(currentAccent, 0.03) }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={7}>
                                <Field
                                  as={TextField} fullWidth variant="filled" size="small"
                                  label={`Behavior ${index + 1}`} name={`behaviors[${index}].name`}
                                  helperText={<ErrorMessage name={`behaviors[${index}].name`}>{msg => <Typography variant="caption" color="error">{msg}</Typography>}</ErrorMessage>}
                                  error={touched.behaviors?.[index]?.name && Boolean(errors.behaviors?.[index]?.name)}
                                  sx={{ '& .MuiFilledInput-root': { borderRadius: BORDER_RADIUS_SMALLER } }}
                                />
                              </Grid>
                              <Grid item xs={9} sm={3}>
                                <Field
                                  as={TextField} fullWidth variant="filled" size="small"
                                  label="Weight (1-10)" type="number" name={`behaviors[${index}].weight`}
                                  inputProps={{ min: 1, max: 10 }}
                                  helperText={<ErrorMessage name={`behaviors[${index}].weight`}>{msg => <Typography variant="caption" color="error">{msg}</Typography>}</ErrorMessage>}
                                  error={touched.behaviors?.[index]?.weight && Boolean(errors.behaviors?.[index]?.weight)}
                                  sx={{ '& .MuiFilledInput-root': { borderRadius: BORDER_RADIUS_SMALLER } }}
                                />
                              </Grid>
                              <Grid item xs={3} sm={2} textAlign="right">
                                <IconButton onClick={() => values.behaviors.length > 1 && remove(index)} disabled={values.behaviors.length <= 1} aria-label="Remove behavior">
                                    <DeleteForeverOutlinedIcon color={values.behaviors.length <= 1 ? "disabled" : "action"}/>
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Paper>
                        ))}
                        <Button
                          variant="outlined" startIcon={<AddIcon />}
                          onClick={() => push({ name: '', weight: 1 })}
                          sx={{ textTransform: 'none', color: currentAccent, borderColor: currentAccent, mt: 1, borderRadius: BORDER_RADIUS_VALUE }}
                        > Add Behavior </Button>
                      </Box>
                    )}
                  </FieldArray>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: activeStep === 0 ? 'flex-end' : 'space-between', mt: 4, pt:2, borderTop: 1, borderColor: 'divider' }}>
              {activeStep !== 0 && (
                <Button
                  onClick={handleBack}
                  variant="outlined"
                  sx={{ borderRadius: BORDER_RADIUS_VALUE, px: 3, borderColor: theme.palette.grey[400], color: theme.palette.text.secondary }}
                > Back </Button>
              )}
              <Button
                type={activeStep === steps.length - 1 ? 'submit' : 'button'}
                onClick={activeStep === steps.length - 1 ? undefined : handleNext} 
                variant="contained"
                disabled={isSubmitting || (activeStep === steps.length -1 && (!dirty || !isValid) && formErrorsSummary.length > 0)}
                sx={{ 
                  borderRadius: BORDER_RADIUS_VALUE, px: 4, 
                  backgroundColor: currentAccent, 
                  color: theme.palette.getContrastText(currentAccent),
                  '&:hover': { backgroundColor: alpha(currentAccent, 0.85) }
                }}
              >
                {isSubmitting ? 'Submitting...' : (activeStep === steps.length - 1 ? 'Create Profile' : 'Next')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
}

CreateIdealCandidateForm.defaultProps = {
    onSubmit: (values) => {
        console.log("Default onSubmit from CreateIdealCandidateForm:", values);
    },
    viewOnly: false,
    initialData: null,
};

